import React, { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router';
import { Box, FiservCardInterface, FiservCardStatusEnum, useResponsive } from '@common-fe/common-fe';

import { ROUTES } from '@/common';
import { Roles } from '@/common/constants';
import AppButton from '@/components/controls/AppButton';
import { useHasAccess } from '@/modules/core/hooks';
import { useShouldShowCardActions } from '@/modules/transaction/components/Cards/hooks/useShouldShowCardActions';
import { useSetCardStatusModal } from '@/modules/transaction/components/Cards/store/useSetCardStatusModal.store';

import useCardActivationModalStore from '../store/useCardActivationModal.store';

import { useRequestedActivationCards } from './CardActivationModals/hooks/useRequestedActivationCards';
import DisableActionHintWrapper from './DisableActionHintWrapper';

interface Props {
  card: FiservCardInterface;
  isLoading?: boolean;
  isDarkMode?: boolean;
  isCardListMode?: boolean;
}

export const CardItemActions: React.FC<Props> = ({ card, isLoading, isDarkMode, isCardListMode }) => {
  const { t } = useTranslation();
  const { push } = useHistory();
  const { status, id } = card;
  const { readyForActivation, disableManage } = useShouldShowCardActions(card);
  const showManage = useMemo(() => {
    return (
      status === FiservCardStatusEnum.ACTIVE ||
      status === FiservCardStatusEnum.PENDING_OPEN ||
      status === FiservCardStatusEnum.PENDING_UNLOCK ||
      status === FiservCardStatusEnum.FROZEN ||
      status === FiservCardStatusEnum.PENDING_FROZEN
    );
  }, [status]);
  const showActive = useMemo(() => status === FiservCardStatusEnum.PENDING_OPEN, [status]);

  const { handleChangeState: cardStatusChangeState } = useSetCardStatusModal();
  const employeeHasLimitedAccess = useHasAccess([
    {
      role: Roles.employeeLimitedAccess,
    },
  ]);

  const {
    handleChangeVisibility: handleChangeActivationVisibility,
    handleChangeCardID: handleChangeActivationCardID,
    handleChangeLast4Numbers,
    handleChangeAlternateAccountId,
    handleChangeMemberSequenceIdentifier,
    handleChangeTokenCardId,
  } = useCardActivationModalStore();

  const setCardActivationInfo = useCallback(() => {
    handleChangeActivationCardID(id);
    handleChangeLast4Numbers(card.last4Number);
    handleChangeAlternateAccountId(card.alternateAccountId);
    handleChangeMemberSequenceIdentifier(card.memberSequenceIdentifier);
    if (card?.tokenCardId) handleChangeTokenCardId(card.tokenCardId);
  }, [
    handleChangeActivationCardID,
    id,
    handleChangeLast4Numbers,
    card,
    handleChangeMemberSequenceIdentifier,
    handleChangeAlternateAccountId,
    handleChangeTokenCardId,
  ]);

  const onCardActivationModal = useCallback(() => {
    setCardActivationInfo();
    handleChangeActivationVisibility(true);
  }, [handleChangeActivationVisibility, setCardActivationInfo]);

  const { isCardRequestedToActivate } = useRequestedActivationCards({
    cardId: id,
    cardStatus: status,
    last4: card.last4Number,
  });

  const handleOpenCardDetails = useCallback(() => {
    push(`${ROUTES.CARD_DETAILS}?dependent_id=${card.holder.dependentId || ''}&card_id=${card.id}`);
  }, [card.holder.dependentId, card.id, push]);
  const { isMobile } = useResponsive();

  return (
    <Box
      direction={isDarkMode && isMobile ? 'column' : 'row'}
      justify="between"
      data-testid="Card-item-actions"
    >
      {showActive && (
        <Box margin={isDarkMode && isMobile ? { bottom: '10px' } : { right: '10px' }} flex>
          <DisableActionHintWrapper
            employeeLimitedAccess={employeeHasLimitedAccess}
            hideHint={
              (readyForActivation && !isCardRequestedToActivate(id) && !employeeHasLimitedAccess) ||
              !!isLoading
            }
            hintElement={
              <AppButton
                width="100%"
                isDarkMode={isDarkMode}
                buttonType={isDarkMode ? 'primary' : 'secondary'}
                onClick={onCardActivationModal}
                disabled={
                  !readyForActivation ||
                  isCardRequestedToActivate(id) ||
                  employeeHasLimitedAccess ||
                  isLoading
                }
              >
                {t('Activate Card')}
              </AppButton>
            }
          />
        </Box>
      )}
      {isCardListMode && (
        <Box flex>
          <AppButton buttonType="secondary" width="100%" disabled={isLoading} onClick={handleOpenCardDetails}>
            {t('Manage Card')}
          </AppButton>
        </Box>
      )}
      {!isCardListMode && showManage && (
        <Box flex>
          <DisableActionHintWrapper
            hideHint={!disableManage || !!isLoading}
            hintElement={
              <AppButton
                isDarkMode={isDarkMode}
                buttonType={isDarkMode ? 'primary' : 'secondary'}
                width="100%"
                disabled={disableManage || isLoading}
                onClick={() => {
                  cardStatusChangeState({
                    cardId: card.id,
                  });
                }}
              >
                {t('Manage Card Access')}
              </AppButton>
            }
          />
        </Box>
      )}
    </Box>
  );
};
